import { useState, useEffect, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Plus, Trash2 } from 'lucide-react'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>
interface Hotspot { handle?: string; label?: string; x?: number; y?: number }

interface ShoppableRoomEditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
}

const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)))

/**
 * Visual editor for a `shoppable_room` — a lifestyle image with product-linked hotspots.
 * The curator drags each pin onto the artwork it represents and assigns the product
 * handle; on approve, the publish adapter writes arthaus.shoppable_room and the storefront
 * "Shop the Room" section renders it. Copy fields (eyebrow/heading/body) tune the text column.
 */
export function ShoppableRoomEditor({ item, onSave, saving }: ShoppableRoomEditorProps) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  const [drag, setDrag] = useState<number | null>(null)
  const imgWrap = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setP((item.payload as Payload) || {})
  }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: any) => setP((prev) => ({ ...prev, [k]: v }))
  const hotspots: Hotspot[] = Array.isArray(p.hotspots) ? p.hotspots : []

  const setHotspot = (i: number, patch: Partial<Hotspot>) =>
    setP((prev) => {
      const hs = [...(Array.isArray(prev.hotspots) ? prev.hotspots : [])]
      hs[i] = { ...hs[i], ...patch }
      return { ...prev, hotspots: hs }
    })

  const addHotspot = () =>
    setP((prev) => ({
      ...prev,
      hotspots: [...(Array.isArray(prev.hotspots) ? prev.hotspots : []), { handle: '', label: '', x: 50, y: 40 }],
    }))

  const removeHotspot = (i: number) =>
    setP((prev) => ({ ...prev, hotspots: (prev.hotspots || []).filter((_: Hotspot, j: number) => j !== i) }))

  // pointer → percentage within the image
  const pointToPct = (e: { clientX: number; clientY: number }) => {
    const el = imgWrap.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    return { x: clamp(((e.clientX - r.left) / r.width) * 100), y: clamp(((e.clientY - r.top) / r.height) * 100) }
  }

  const onMove = (e: React.PointerEvent) => {
    if (drag === null) return
    const pt = pointToPct(e)
    if (pt) setHotspot(drag, pt)
  }

  return (
    <div className="space-y-5">
      {/* Visual placement surface */}
      {p.image_url ? (
        <div
          ref={imgWrap}
          className="relative w-full rounded-md border overflow-hidden select-none touch-none bg-muted"
          style={{ aspectRatio: '4 / 3' }}
          onPointerMove={onMove}
          onPointerUp={() => setDrag(null)}
          onPointerLeave={() => setDrag(null)}
        >
          <img src={String(p.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
          {hotspots.map((h, i) => (
            <button
              key={i}
              type="button"
              onPointerDown={(e) => { e.preventDefault(); setDrag(i) }}
              className={`absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 border-white shadow-md grid place-items-center text-[11px] font-semibold text-white cursor-grab active:cursor-grabbing ${drag === i ? 'bg-primary ring-2 ring-primary/40' : 'bg-amber-700/90'}`}
              style={{ left: `${h.x ?? 50}%`, top: `${h.y ?? 40}%` }}
              title={h.label || h.handle || `Hotspot ${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      ) : (
        <Field label="Lifestyle image URL (required)">
          <Input value={String(p.image_url || '')} onChange={(e) => set('image_url', e.target.value)} placeholder="https://cdn.shopify.com/.../room.jpg" />
        </Field>
      )}
      <p className="text-xs text-muted-foreground -mt-2">Drag each numbered pin onto the artwork it links to.</p>

      {/* Hotspot rows */}
      <Field label={`Hotspots (${hotspots.length})`}>
        <div className="space-y-2">
          {hotspots.map((h, i) => (
            <div key={i} className="flex items-center gap-2 border rounded-md px-2 py-1.5">
              <span className="w-6 h-6 shrink-0 rounded-full bg-amber-700/90 text-white grid place-items-center text-[11px] font-semibold">{i + 1}</span>
              <Input className="flex-1" value={String(h.handle || '')} onChange={(e) => setHotspot(i, { handle: e.target.value })} placeholder="product handle (e.g. orphic)" />
              <Input className="w-40" value={String(h.label || '')} onChange={(e) => setHotspot(i, { label: e.target.value })} placeholder="label" />
              <span className="text-xs text-muted-foreground tabular-nums w-20 text-center">{h.x ?? 50},{h.y ?? 40}</span>
              <Button type="button" variant="ghost" size="icon" onClick={() => removeHotspot(i)} aria-label="Remove hotspot"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addHotspot}><Plus className="w-4 h-4 mr-1.5" /> Add hotspot</Button>
        </div>
      </Field>

      {/* Copy + image source */}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Lifestyle image URL"><Input value={String(p.image_url || '')} onChange={(e) => set('image_url', e.target.value)} /></Field>
        <Field label="Arrangement"><Input value={String(p.arrangement || '')} onChange={(e) => set('arrangement', e.target.value)} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Eyebrow"><Input value={String(p.eyebrow || '')} onChange={(e) => set('eyebrow', e.target.value)} /></Field>
        <Field label="Heading"><Input value={String(p.heading || '')} onChange={(e) => set('heading', e.target.value)} /></Field>
      </div>
      <Field label="Body"><Textarea rows={3} value={String(p.body || '')} onChange={(e) => set('body', e.target.value)} /></Field>

      <div className="flex justify-end">
        <Button onClick={() => onSave(p)} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
